import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { Lock, LogOut, Inbox, RefreshCw, Mail, Building2, ArrowLeft, Loader2, FileText, Download, ChevronLeft, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ev_admin_token";
const STAGES = ["New", "Reviewed", "Qualified", "Site Visit", "Monitoring", "Proposal", "Won", "Lost"];
const OPPORTUNITIES = ["Electricity Optimisation", "Gas", "Water", "Monitoring", "Solar PV", "Battery Storage", "Controls", "Multi-site CII", "Further Investigation"];
const STAGE_COLOR = {
  New: "#22A9E0", Reviewed: "#7C9CBF", Qualified: "#E5A13B", "Site Visit": "#E5A13B",
  Monitoring: "#8B5CF6", Proposal: "#E5A13B", Won: "#22C55E", Lost: "#EF4444",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-sm border border-ev-teal/15 bg-ev-bg/60 px-4 py-3.5 text-white placeholder:text-ev-muted/60 outline-none transition-colors duration-300 focus:border-ev-teal focus:ring-2 focus:ring-ev-teal/20";

  return (
    <div className="flex min-h-screen items-center justify-center bg-ev-bg px-6">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-ev-teal/10 blur-[130px]" />
      <form
        onSubmit={submit}
        data-testid="login-form"
        className="relative w-full max-w-md rounded-sm border border-ev-teal/15 bg-ev-surface/60 p-8 backdrop-blur-xl md:p-10"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-sm border border-ev-teal/25 text-ev-teal">
          <Lock size={18} />
        </div>
        <div className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase text-ev-teal/80">
          Energy Vault Group
        </div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-white">Leads Dashboard</h1>
        <p className="mt-2 text-sm text-ev-muted">Sign in to view free-report requests.</p>

        <div className="mt-8 space-y-5">
          <div>
            <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-ev-muted">Email</label>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="you@energyvaultgroup.co.uk" required />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-ev-muted">Password</label>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} placeholder="••••••••" required />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-ev-teal px-7 py-3.5 font-mono text-[12px] tracking-[0.14em] uppercase text-ev-bg font-semibold transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign In"}
        </button>

        <a href="/" data-testid="back-home" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-ev-muted hover:text-ev-teal transition-colors">
          <ArrowLeft size={13} /> Back to site
        </a>
      </form>
    </div>
  );
}

function StatusBadge({ status }) {
  const c = STAGE_COLOR[status] || "#7C9CBF";
  return (
    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-[10px] tracking-[0.14em] uppercase" style={{ color: c, border: `1px solid ${c}55`, background: `${c}14` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} /> {status}
    </span>
  );
}

function gapName(g) {
  return `${g.client_first_name} ${g.client_last_name || ""}`.trim();
}

function exportGapPdf(ga) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(11, 21, 51); doc.rect(0, 0, W, 92, "F");
  doc.setFillColor(229, 161, 59); doc.rect(0, 92, W, 4, "F");
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text("Energy Vault Group", 40, 44);
  doc.setFontSize(12); doc.setTextColor(34, 169, 224);
  doc.text("Building Intelligence Gap Analysis", 40, 66);
  let y = 124;
  const name = gapName(ga);
  doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text(name + (ga.company_name ? ` — ${ga.company_name}` : ""), 40, y); y += 18;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90, 90, 90);
  doc.text(`${ga.client_email}    ·    Status: ${ga.status}    ·    ${new Date(ga.created_at).toLocaleString("en-GB")}`, 40, y); y += 10;
  if (ga.opportunities && ga.opportunities.length) {
    y += 14; doc.setFont("helvetica", "bold"); doc.setTextColor(229, 161, 59);
    doc.text(`Identified Opportunity: ${ga.opportunities.join(", ")}`, 40, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(90, 90, 90);
  }
  if (ga.bill_filename) { y += 14; doc.text(`Bill attached: ${ga.bill_filename}`, 40, y); }
  y += 22;
  (ga.sections || []).forEach((sec) => {
    const items = (sec.fields || []).filter((f) => f.value && f.value.trim());
    if (!items.length) return;
    if (y > 760) { doc.addPage(); y = 50; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(34, 169, 224);
    doc.text(sec.title.toUpperCase(), 40, y); y += 6;
    doc.setDrawColor(215); doc.line(40, y, W - 40, y); y += 16;
    items.forEach((f) => {
      const label = doc.splitTextToSize(f.label, W - 80);
      const val = doc.splitTextToSize(f.value, W - 80);
      if (y + (label.length + val.length) * 12 > 800) { doc.addPage(); y = 50; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(30, 30, 30);
      doc.text(label, 40, y); y += label.length * 12;
      doc.setFont("helvetica", "normal"); doc.setTextColor(70, 70, 70);
      doc.text(val, 40, y); y += val.length * 12 + 9;
    });
    y += 8;
  });
  doc.save(`GapAnalysis_${name.replace(/\s+/g, "_") || "submission"}.pdf`);
}

function GapDetail({ ga, onBack, onPatch }) {
  const token = localStorage.getItem(TOKEN_KEY);
  const billUrl = ga.bill_path ? `${API}/gap-analysis/${ga.id}/bill?auth=${token}` : null;
  const opps = ga.opportunities || [];
  const toggleOpp = (o) => onPatch(ga.id, { opportunities: opps.includes(o) ? opps.filter((x) => x !== o) : [...opps, o] });
  return (
    <div data-testid="gap-detail">
      <button onClick={onBack} data-testid="gap-back" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-ev-muted hover:text-ev-teal transition-colors">
        <ChevronLeft size={14} /> Back to pipeline
      </button>
      <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="font-display text-3xl font-bold text-white">{gapName(ga)}</div>
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            <a href={`mailto:${ga.client_email}`} className="flex items-center gap-2 text-ev-teal hover:text-ev-gold"><Mail size={13} /> {ga.client_email}</a>
            {ga.company_name && <span className="flex items-center gap-2 text-ev-muted"><Building2 size={13} /> {ga.company_name}</span>}
            <span className="font-mono text-[11px] uppercase text-ev-muted">{fmtDate(ga.created_at)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {billUrl && (
            <a href={billUrl} target="_blank" rel="noreferrer" data-testid="gap-bill-link" className="flex items-center gap-2 rounded-full border border-ev-teal/25 px-4 py-2 font-mono text-[11px] tracking-[0.14em] uppercase text-ev-teal hover:border-ev-teal/60 transition-colors">
              <Paperclip size={13} /> View Bill
            </a>
          )}
          <button onClick={() => exportGapPdf(ga)} data-testid="gap-pdf" className="flex items-center gap-2 rounded-full bg-ev-gold px-4 py-2 font-mono text-[11px] tracking-[0.14em] uppercase text-ev-bg font-semibold hover:-translate-y-0.5 transition-transform">
            <Download size={13} /> Export PDF
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-sm border border-ev-teal/12 bg-ev-surface/40 p-5">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ev-muted mb-3">Pipeline stage</div>
        <div className="flex flex-wrap gap-2" data-testid="gap-stages">
          {STAGES.map((s) => (
            <button key={s} onClick={() => onPatch(ga.id, { status: s })} data-testid={`gap-stage-${s.replace(/\s+/g, "-").toLowerCase()}`}
              className="rounded-full px-3.5 py-1.5 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors"
              style={ga.status === s ? { background: STAGE_COLOR[s], color: "#0B1533", fontWeight: 700 } : { border: "1px solid rgba(255,255,255,.15)", color: "#8B9BB4" }}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-sm border border-ev-gold/25 bg-ev-gold/[0.06] p-5" data-testid="gap-opportunities">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ev-gold">Identified Opportunity</span>
          <span className="font-mono text-[9px] tracking-[0.14em] uppercase text-ev-muted">· Internal · your diagnosis</span>
        </div>
        <p className="text-xs text-ev-muted mb-3">The client gives us the facts — you diagnose the opportunity after reviewing their Gap Analysis.</p>
        <div className="flex flex-wrap gap-2">
          {OPPORTUNITIES.map((o) => (
            <button key={o} onClick={() => toggleOpp(o)} data-testid={`gap-opp-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className="rounded-full px-3.5 py-1.5 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors"
              style={opps.includes(o) ? { background: "#E5A13B", color: "#0B1533", fontWeight: 700 } : { border: "1px solid rgba(229,161,59,.35)", color: "#C9A24B" }}>
              {o}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 space-y-8">
        {(ga.sections || []).map((sec) => {
          const items = (sec.fields || []).filter((f) => f.value && f.value.trim());
          if (!items.length) return null;
          return (
            <div key={sec.title}>
              <div className="font-display text-lg font-bold text-ev-teal border-b border-ev-teal/15 pb-2">{sec.title}</div>
              <div className="mt-4 grid gap-x-8 gap-y-4 md:grid-cols-2">
                {items.map((f) => (
                  <div key={f.label}>
                    <div className="text-xs text-ev-muted leading-snug">{f.label}</div>
                    <div className="mt-1 text-sm text-white leading-relaxed whitespace-pre-wrap">{f.value}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("gap");
  const [leads, setLeads] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [c, g] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/gap-analysis`, { headers }),
      ]);
      setLeads(c.data);
      setGaps(g.data);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please sign in again."); onLogout(); }
      else { toast.error("Failed to load data."); }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const patchGap = async (id, changes) => {
    const token = localStorage.getItem(TOKEN_KEY);
    setGaps((prev) => prev.map((g) => (g.id === id ? { ...g, ...changes } : g)));
    try {
      await axios.patch(`${API}/gap-analysis/${id}`, changes, { headers: { Authorization: `Bearer ${token}` } });
      if (changes.status) toast.success(`Moved to ${changes.status}.`);
      else toast.success("Opportunity updated.");
    } catch {
      toast.error("Could not save change.");
      load();
    }
  };

  const selected = gaps.find((g) => g.id === selectedId);
  const tabCls = (t) => `rounded-full px-5 py-2 font-mono text-[11px] tracking-[0.14em] uppercase transition-colors ${tab === t ? "bg-ev-teal text-ev-bg font-semibold" : "border border-ev-teal/20 text-ev-muted hover:text-ev-teal"}`;

  return (
    <div className="min-h-screen bg-ev-bg">
      <header className="sticky top-0 z-10 border-b border-ev-teal/10 bg-ev-bg/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <span className="relative flex h-8 w-8 items-center justify-center">
              <span className="absolute inset-0 rounded-sm border border-ev-teal/40 rotate-45" />
              <span className="h-1.5 w-1.5 rounded-full bg-ev-gold" />
            </span>
            <div>
              <div className="font-display font-extrabold tracking-tight text-sm leading-none text-white">DASHBOARD</div>
              <div className="font-mono text-[9px] tracking-[0.24em] text-ev-muted mt-0.5">ENERGY VAULT GROUP</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-2 rounded-full border border-ev-teal/20 px-4 py-2 font-mono text-[11px] tracking-[0.14em] uppercase text-ev-muted hover:text-ev-teal hover:border-ev-teal/50 transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={onLogout} data-testid="logout-btn" className="flex items-center gap-2 rounded-full bg-ev-navy/60 px-4 py-2 font-mono text-[11px] tracking-[0.14em] uppercase text-white hover:bg-ev-navy transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
        <div className="flex flex-wrap items-center gap-3">
          <button className={tabCls("gap")} onClick={() => { setTab("gap"); setSelectedId(null); }} data-testid="tab-gap">Gap Analysis · {gaps.length}</button>
          <button className={tabCls("enquiries")} onClick={() => setTab("enquiries")} data-testid="tab-enquiries">Enquiries · {leads.length}</button>
        </div>

        {loading ? (
          <div className="mt-16 flex items-center justify-center text-ev-muted"><Loader2 className="animate-spin" size={22} /></div>
        ) : tab === "enquiries" ? (
          leads.length === 0 ? (
            <div data-testid="empty-state" className="mt-16 flex flex-col items-center justify-center rounded-sm border border-dashed border-ev-teal/15 py-20 text-center">
              <Inbox className="text-ev-muted" size={36} />
              <p className="mt-4 font-display text-xl text-white">No enquiries yet</p>
              <p className="mt-1 text-sm text-ev-muted">New submissions from the contact form will appear here.</p>
            </div>
          ) : (
            <div className="mt-10 space-y-4" data-testid="leads-list">
              {leads.map((l, i) => (
                <div key={l.id} data-testid={`lead-row-${i}`} className="group rounded-sm border border-ev-teal/12 bg-ev-surface/50 p-6 transition-colors duration-300 hover:border-ev-teal/30">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="font-display text-xl font-semibold text-white">{l.name}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                        <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-ev-teal hover:text-ev-gold transition-colors"><Mail size={13} /> {l.email}</a>
                        {l.company && <span className="flex items-center gap-2 text-ev-muted"><Building2 size={13} /> {l.company}</span>}
                      </div>
                    </div>
                    <div className="font-mono text-[11px] tracking-[0.1em] uppercase text-ev-muted whitespace-nowrap">{fmtDate(l.created_at)}</div>
                  </div>
                  <p className="mt-4 border-t border-ev-teal/10 pt-4 text-sm text-ev-muted leading-relaxed">{l.message}</p>
                </div>
              ))}
            </div>
          )
        ) : selected ? (
          <div className="mt-8"><GapDetail ga={selected} onBack={() => setSelectedId(null)} onPatch={patchGap} /></div>
        ) : gaps.length === 0 ? (
          <div data-testid="gap-empty" className="mt-16 flex flex-col items-center justify-center rounded-sm border border-dashed border-ev-teal/15 py-20 text-center">
            <FileText className="text-ev-muted" size={36} />
            <p className="mt-4 font-display text-xl text-white">No Gap Analysis submissions yet</p>
            <p className="mt-1 text-sm text-ev-muted">Completed questionnaires from /gap-analysis will appear here.</p>
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="pipeline-summary">
              {STAGES.map((s) => {
                const n = gaps.filter((g) => (g.status || "New") === s).length;
                return (
                  <div key={s} className="flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-mono" style={{ border: `1px solid ${STAGE_COLOR[s]}44`, color: STAGE_COLOR[s] }}>
                    {s} <span className="text-white">{n}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 space-y-4" data-testid="gap-list">
              {gaps.map((g, i) => (
                <button key={g.id} onClick={() => setSelectedId(g.id)} data-testid={`gap-row-${i}`}
                  className="block w-full text-left group rounded-sm border border-ev-teal/12 bg-ev-surface/50 p-6 transition-colors duration-300 hover:border-ev-teal/40">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="font-display text-xl font-semibold text-white">{gapName(g)}{g.company_name ? <span className="text-ev-muted font-normal"> · {g.company_name}</span> : null}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                        <span className="flex items-center gap-2 text-ev-teal"><Mail size={13} /> {g.client_email}</span>
                        {g.bill_filename && <span className="flex items-center gap-2 text-ev-muted"><Paperclip size={13} /> Bill attached</span>}
                      </div>
                      {g.opportunities && g.opportunities.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`gap-row-opps-${i}`}>
                          {g.opportunities.map((o) => (
                            <span key={o} className="rounded-full px-2.5 py-0.5 text-[10px] font-mono tracking-[0.08em]" style={{ color: "#E5A13B", border: "1px solid rgba(229,161,59,.35)", background: "rgba(229,161,59,.08)" }}>{o}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <StatusBadge status={g.status || "New"} />
                      <span className="font-mono text-[11px] uppercase text-ev-muted whitespace-nowrap">{fmtDate(g.created_at)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default function LeadsDashboard() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
